import { cn } from '../../../utils/cn';
import { formatEUR } from '../../../utils/money';

/**
 * .pen: CHMbm (ds/label/section-heading) — usado 3x no Relatório, acima das
 * camadas 2/3/4. Eyebrow maiúsculo + hint muted opcional.
 */
export function ReportSectionHeading({ eyebrow, hint, id }) {
  return (
    <h2
      id={id}
      className="px-1 text-type-section font-bold uppercase tracking-[0.09em] text-muted-foreground"
    >
      {eyebrow}
      {hint ? <span className="font-normal normal-case"> {hint}</span> : null}
    </h2>
  );
}

const MESSAGE_TONE = {
  warning: {
    dsId: 'ds/message--warning',
    shell: 'border-warning-500 bg-warning-tint',
    icon: 'text-warning-fg',
    title: 'text-warning-fg',
  },
  neutral: {
    dsId: 'ds/message--neutral',
    shell: 'border-border bg-muted/60',
    icon: 'text-muted-foreground',
    title: 'text-foreground',
  },
};

/**
 * .pen: t2m6wT (ds/message--warning) / idjom (ds/message--neutral) — usado no
 * Relatório para o aviso de geração demorada, o banner "sem veredito" e o
 * disclaimer de rodapé.
 */
export function ReportMessage({ tone = 'neutral', icon: Icon, title, children, action, className }) {
  const style = MESSAGE_TONE[tone] || MESSAGE_TONE.neutral;
  return (
    <div
      data-ds-id={style.dsId}
      className={cn(
        'flex flex-col gap-space-3 rounded-ds-lg border p-space-4',
        style.shell,
        className,
      )}
    >
      <div className="flex items-start gap-space-3">
        {Icon ? <Icon className={cn('mt-0.5 size-5 shrink-0', style.icon)} aria-hidden /> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-space-1">
          {title ? (
            <p className={cn('m-0 text-type-body font-semibold leading-snug', style.title)}>{title}</p>
          ) : null}
          {children ? (
            <p className="m-0 text-type-body-sm leading-[1.45] text-muted-foreground">{children}</p>
          ) : null}
        </div>
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

const SUB_TONE_CLASS = {
  positive: 'text-emerald-700 dark:text-emerald-400',
  negative: 'text-red-700 dark:text-red-400',
  warning: 'text-amber-700 dark:text-amber-400',
};

/**
 * .pen: "grid" dentro de cada secção do accordion (Património, Resumo
 * financeiro do ciclo) — cartões `$surface-muted` 2 ou 4 colunas.
 */
export function ReportSummaryGrid({ items, columns = 2 }) {
  if (!items?.length) return null;
  const colClass = columns === 4 ? 'grid-cols-2' : 'grid-cols-2';
  return (
    <div className={cn('grid gap-space-3', colClass)}>
      {items.map((item) => (
        <div key={item.label} className="rounded-ds-md bg-muted px-space-3 py-space-2.5">
          <p className="text-type-section font-bold uppercase tracking-[0.06em] text-muted-foreground">
            {item.label}
          </p>
          <p className="mt-0.5 text-type-display-row font-semibold tabular-nums text-foreground">
            {item.value}
          </p>
          {item.sub ? (
            <p
              className={cn(
                'mt-0.5 text-type-label tabular-nums',
                item.subTone ? SUB_TONE_CLASS[item.subTone] : 'text-muted-foreground',
              )}
            >
              {item.sub}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * .pen: "hero" dentro de "acc · Património" — label pequeno + valor grande +
 * delta, sem casca própria (fica dentro do content do accordion).
 */
export function ReportHero({ label, value, changeLabel, changePositive }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-type-section font-bold uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </p>
      <p className="text-type-display-kpi font-semibold tracking-tight tabular-nums text-foreground">
        {formatEUR(value)}
      </p>
      {changeLabel ? (
        <p
          className={cn(
            'pt-space-1 text-type-body-sm font-medium',
            changePositive === false ? 'text-danger-fg' : 'text-success-fg',
          )}
        >
          {changeLabel}
        </p>
      ) : null}
    </div>
  );
}

export function ReportPanel({ children, className }) {
  return (
    <div className={cn('rounded-ds-lg bg-muted/60 px-space-4 py-space-5', className)}>{children}</div>
  );
}

const BUDGET_BAR_CLASS = {
  ok: 'bg-sage-500',
  exceeded: 'bg-danger-500',
  no_budget: 'bg-muted-foreground/30',
};

const BUDGET_VALUE_CLASS = {
  ok: 'text-muted-foreground',
  exceeded: 'text-danger-fg',
  no_budget: 'text-muted-foreground',
};

/**
 * .pen: linhas `ds/row/category-progress` (r40CS) dentro de "acc ·
 * Categorias" e "acc · Objetivos" — nome + valor/limite + barra + nota de
 * rodapé opcional. Reutiliza a track/fill do primitivo de progresso
 * partilhado (CategoryBudgetProgressBar), compõe nome+valor à volta.
 */
export function ReportProgressRow({ name, value, status = 'ok' }) {
  const pct = Math.min(100, Math.max(0, value?.pct ?? 0));
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-space-3">
        <span className="text-type-body-sm font-medium text-foreground">{name}</span>
        <span className={cn('text-type-body-sm tabular-nums', BUDGET_VALUE_CLASS[status])}>
          {value?.label}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-ds-full bg-muted">
        <div
          className={cn('h-full rounded-ds-full', BUDGET_BAR_CLASS[status] || BUDGET_BAR_CLASS.no_budget)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function ReportProgressList({ items, footnote }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-col gap-space-4">
      {items.map((item) => (
        <ReportProgressRow key={item.id} name={item.name} value={item.value} status={item.status} />
      ))}
      {footnote ? (
        <p className="border-t border-border pt-space-3 text-type-label text-muted-foreground">
          {footnote}
        </p>
      ) : null}
    </div>
  );
}

/**
 * .pen: linhas `ds/row/record-item` (FQhuw) dentro de "acc · Maiores
 * despesas" — nome + subtítulo (data · categoria) + valor + barra
 * proporcional de largura fixa.
 */
export function ReportExpenseRow({ title, subtitle, amount, barWidth }) {
  return (
    <div className="flex items-center justify-between gap-space-3 border-b border-border py-space-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-type-body-sm font-medium text-foreground">{title}</p>
        <p className="text-type-label text-muted-foreground">{subtitle}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-type-body-sm font-medium tabular-nums text-foreground">{amount}</span>
        <span className="h-[3px] w-[120px] overflow-hidden rounded-ds-full bg-muted">
          <span
            className="block h-full rounded-ds-full bg-sage-500"
            style={{ width: `${Math.min(100, barWidth)}%` }}
          />
        </span>
      </div>
    </div>
  );
}

const META_CHIP_VALUE_TONE = {
  positive: 'text-success-fg',
  caution: 'text-warning-fg',
  neutral: 'text-foreground',
};

/**
 * .pen: JgYO8 (ds/selfie/meta-chip) — ícone + label + valor, pill
 * arredondada. Usado na Camada 1 (veredito).
 */
export function ReportMetaChip({ icon: Icon, label, value, tone = 'neutral' }) {
  return (
    <div className="flex items-center gap-space-1 rounded-ds-full border border-border bg-muted px-space-2.5 py-space-1">
      {Icon ? <Icon className="size-3 shrink-0 text-muted-foreground" aria-hidden /> : null}
      <span className="text-type-chip text-muted-foreground">{label}</span>
      <span className={cn('text-type-chip font-semibold', META_CHIP_VALUE_TONE[tone])}>{value}</span>
    </div>
  );
}

const ASSESS_TONE = {
  good: { chip: 'bg-sage-tint text-sage-on-tint', label: 'Bem' },
  bad: { chip: 'bg-warning-tint text-warning-fg', label: 'Atenção' },
  improve: { chip: 'bg-muted text-muted-foreground', label: 'Melhorar' },
};

/**
 * .pen: item dentro de "acc · Avaliação completa" / "acc · Organização de
 * categorias" (frames `Selfie · Relatório · Pronto · *`) — lista densa com
 * separador `border-t` entre itens (não o primeiro), chip de tom largura
 * fixa + título/corpo. Tons partilhados com `ds/selfie/highlight-card`.
 */
export function ReportAssessCard({ tone, title, body, first = false }) {
  const style = ASSESS_TONE[tone] || ASSESS_TONE.improve;

  return (
    <div className={cn('flex items-start gap-space-2.5 py-space-3', !first && 'border-t border-border')}>
      <span
        className={cn(
          // eslint-disable-next-line no-restricted-syntax -- largura fixa 78px do .pen, sem token
          'flex w-[78px] shrink-0 items-center justify-center rounded-ds-sm py-0.5 text-center text-type-section font-bold uppercase tracking-[0.08em]',
          style.chip,
        )}
      >
        {style.label}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-type-body-sm font-semibold leading-snug text-foreground">{title}</p>
        {body ? (
          <p className="mt-0.5 text-type-label leading-[1.45] text-muted-foreground">{body}</p>
        ) : null}
      </div>
    </div>
  );
}
